import { IDoctor, InstitutionType } from '@alberta/konexi-shared';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Capacitor } from '@capacitor/core';
import { validateEmail } from '@common/email/email-validator';
import { DoctorDB } from 'src/app/common/repository/databases';
import { InfoBoxType } from 'src/app/shared/components/common/info-box/info-box.component';
import { SelectionItem } from 'src/app/shared/models/selectionItem';
import { AuthService } from 'src/app/shared/services/auth.service';
import { InstitutionAttributeService } from 'src/app/shared/services/institution-attribute.service';
import { QueryService } from 'src/app/shared/services/query/query.service';
import { InstitutionAttributeConfigService } from 'src/app/shared/services/tenant-config/institution-attribute-config.service';

@Component({
  selector: 'swodoc-node-medical-order-data',
  templateUrl: 'swodoc-node-medical-order-data.html',
  styleUrls: ['swodoc-node-medical-order-data.scss'],
  providers: [InstitutionAttributeConfigService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SwodocNodeMedicalOrderData implements OnInit {
  @Input()
  node: any;

  get medicalOrderData() {
    return this.node?.responses[0]?.value;
  }

  get shouldShowCorrespondenceAddress() {
    return this.medicalOrderData?.correspondenceType !== 'Drucken';
  }

  set medicalOrderData(value) {
    this.node.responses[0].value = value;
  }

  public correspondenceTypes: SelectionItem[] = [];
  public doctor: IDoctor;
  public correspondenceNotVerified: boolean;
  public InfoBoxType = InfoBoxType;
  public correspondenceAddressForm = new FormGroup({
    to: new FormControl(undefined),
  });
  private _userEmail: string;

  constructor(
    private _institutionAttributeConfigService: InstitutionAttributeConfigService,
    private _institutionAttributeService: InstitutionAttributeService,
    private _queryService: QueryService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _auth: AuthService
  ) {}

  async ngOnInit() {
    this._userEmail = await this._auth.getUserEmail();
    this.node.mandatory = true;
    await this.doctorChanged(this.medicalOrderData.doctorId);
    const institutionAttributeConfig = await this._institutionAttributeConfigService.getAttributesForInstitutionType(
      InstitutionType.Doctor
    );
    if (institutionAttributeConfig?.length) {
      const correspondenceConfig = institutionAttributeConfig.find(attribute => attribute.name === 'Korrespondenzart');
      if (correspondenceConfig?.values?.length) {
        correspondenceConfig.values.forEach(correspondenceType => {
          this.correspondenceTypes.push(new SelectionItem(correspondenceType, correspondenceType));
        });
      }
    }
    if (!Capacitor.isNativePlatform()) {
      this.correspondenceTypes.push(new SelectionItem('Drucken', 'Drucken'));
    }
    this._changeDetectorRef.markForCheck();
  }

  async ngOnChanges() {
    // we need to manually trigger the validators here again after navigating back to this page
    await this.reValidateCorrespondenceAddress();
  }

  private async reValidateCorrespondenceAddress() {
    if (!this.medicalOrderData?.correspondenceType) {
      return;
    }
    switch (this.medicalOrderData.correspondenceType) {
      case 'Fax':
        this.correspondenceAddressForm.controls.to.setValidators(Validators.required);
        break;
      case 'E-Mail an mich':
        this.correspondenceAddressForm.controls.to.setValidators([Validators.required, validateEmail]);
        break;
      case 'Drucken':
        // no validation needed
        this.correspondenceAddressForm.controls.to.clearValidators();
        this.correspondenceAddressForm.controls.to.setValidators(null);
        break;
      default:
        this.correspondenceAddressForm.controls.to.setValidators([Validators.required, validateEmail]);
        break;
    }
    this.correspondenceAddressForm.controls.to.updateValueAndValidity();
    this.correspondenceAddressForm.controls.to.markAsTouched();
  }

  public async doctorChanged(doctorId: string) {
    this.doctor = await this._queryService.get<IDoctor>(doctorId, DoctorDB);
    const correspondenceType = this.medicalOrderData.correspondenceType
      ? this.medicalOrderData.correspondenceType
      : await this._institutionAttributeService.getCorrespondenceTypeForInstitutionId(doctorId);
    const isNewDoctor = this.medicalOrderData.previousDoctorId !== doctorId;

    if (this.medicalOrderData.correspondenceType !== correspondenceType || isNewDoctor) {
      this.medicalOrderData.correspondenceType = correspondenceType;
      this.correspondenceTypeChanged(correspondenceType);
    }
    this.medicalOrderData.previousDoctorId = doctorId;
  }

  public correspondenceTypeChanged(correspondenceType: string) {
    let to: string;
    this.correspondenceNotVerified = false;
    switch (correspondenceType) {
      case 'Fax':
        this.correspondenceAddressForm.controls.to.setValidators(Validators.required);
        if (this.doctor?.draft?.faxPrescriptionRequest) {
          this.correspondenceNotVerified = true;
          to = this.doctor.draft.faxPrescriptionRequest;
        } else if (this.doctor?.faxPrescriptionRequest) {
          to = this.doctor.faxPrescriptionRequest;
        } else if (this.doctor?.draft?.fax) {
          this.correspondenceNotVerified = true;
          to = this.doctor.draft.fax;
        } else if (this.doctor?.fax) {
          to = this.doctor.fax;
        }
        break;
      case 'E-Mail an mich':
        this.correspondenceAddressForm.controls.to.setValidators([Validators.required, validateEmail]);
        to = this._userEmail;
        break;
      case 'Drucken':
        this.correspondenceAddressForm.controls.to.clearValidators();
        this.correspondenceAddressForm.controls.to.setValue(undefined);
        to = undefined;
        break;
      default:
        this.correspondenceAddressForm.controls.to.setValidators([Validators.required, validateEmail]);
        if (this.doctor?.draft?.emailPrescriptionRequest) {
          this.correspondenceNotVerified = true;
          to = this.doctor.draft.emailPrescriptionRequest;
        } else if (this.doctor?.emailPrescriptionRequest) {
          to = this.doctor.emailPrescriptionRequest;
        } else if (this.doctor?.draft?.email) {
          this.correspondenceNotVerified = true;
          to = this.doctor.draft.email;
        } else if (this.doctor?.email) {
          to = this.doctor.email;
        }
        break;
    }
    this.medicalOrderData.to = to;
    this.correspondenceAddressForm.controls.to.updateValueAndValidity();

    this._changeDetectorRef.markForCheck();
  }
}
